import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, Box, Button, 
  Divider, Chip, Grid, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon,
  useMediaQuery, useTheme, Zoom, Fade, Fab,Badge
} from '@mui/material';
import { 
  CheckCircle, LocalShipping, Payment, 
  Store, Print, ShoppingBag, ArrowBack,
  Discount, Savings, Games, ShoppingCart,
  RestaurantMenu, AccessTime, LocationOn,
  CreditCard, Receipt, EmojiEvents
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import api from '../services/api';
import noPhoto from "../assets/no_photo.webp";
import MultiplayerTicTacToe from '../components/MultiplayerTicTacToe';
import { useAuth } from '../context/AuthContext';
import Lottie from 'lottie-react';
// Import your Lottie JSON file (download from lottiefiles.com)
import successAnimation from '../assets/success-checkmark.json';

const OrderConfirmation = ({keywordsData, language}) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameOpen, setGameOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Check scroll position for sticky buttons
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      setIsSticky(scrollPosition < documentHeight - 200);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate total savings from order items
  const totalSavings = order?.items?.reduce((total, item) => {
    return total + ((item.original_price || item.price) - item.price) * item.quantity;
  }, 0) || 0;

  // Calculate original total before discounts
  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  if (loading) return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      textAlign: 'center',
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      <Typography variant="h6" sx={{ color: '#B3B3B3' }}>Loading order details...</Typography>
    </Container>
  );

  const statusTxt = {
    canceled: language === 'ar' ? 'ملغي' : 'Cancelled',
    pending: language === 'ar' ? 'معلق' : 'Pending',
    processing: language === 'ar' ? 'قيد التحضير' : 'Preparation',
    shipped: language === 'ar' ? 'قيد التوصيل' : 'On the way',
    delivered: language === 'ar' ? 'مكتمل' : 'Completed'
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered':
        return { bg: '#00C853', text: '#000000' };
      case 'canceled':
        return { bg: '#FF3D00', text: '#FFFFFF' };
      case 'processing':
        return { bg: '#FFD700', text: '#000000' };
      case 'shipped':
        return { bg: '#2196F3', text: '#FFFFFF' };
      default:
        return { bg: '#2D2D2D', text: '#FFFFFF' };
    }
  };
  
  if (!order) return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      textAlign: 'center',
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      <Typography variant="h6" sx={{ color: '#FF3D00' }}>{language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found'}</Typography>
      <Button 
        variant="contained" 
        sx={{ 
          mt: 2,
          backgroundColor: '#FFD700',
          color: '#000000',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: '#FFE55C',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
          }
        }} 
        onClick={() => navigate('/')}
      >
        {language === 'ar' ? 'إستمرار التسوق' : 'Continue Shopping'}
      </Button>
    </Container>
  );

  return (
     <Container maxWidth="lg" sx={{ 
      py: { xs: 2, sm: 3, md: 4 }, 
      px: { xs: 1.5, sm: 2, md: 3 },
      mb: { xs: 10, sm: 12 },
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
 {/* Header Section with Lottie Animation and Background */}
<Fade in timeout={500}>
  <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
    {/* Animated Background Circle */}
    <Box sx={{ 
      position: 'relative',
      display: 'inline-block',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: 160, sm: 220, md: 270, lg: 320 },
        height: { xs: 160, sm: 220, md: 270, lg: 320 },
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)',
        animation: 'pulse 2s ease-in-out infinite',
      }
    }}>
      <Box sx={{ 
        width: { xs: 140, sm: 200, md: 250, lg: 300 },
        height: { xs: 140, sm: 200, md: 250, lg: 300 },
        margin: '0 auto',
        mb: 3,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1
      }}>
        <Lottie 
          animationData={successAnimation}
          loop={false}
          autoplay={true}
          style={{ width: '100%', height: '100%' }}
          onComplete={() => console.log('Animation completed')}
        />
      </Box>
    </Box>
    
    <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" gutterBottom sx={{ 
      color: '#FFD700',
      fontWeight: 900,
      letterSpacing: '2px',
      fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
      animation: 'fadeInUp 0.6s ease-out'
    }}>
      {language === 'ar' ? 'تم تأكيد الطلب !' : 'Order confirmed!'}
    </Typography>
    
    <Typography 
      variant="h6" 
      sx={{ 
        color: '#B3B3B3', 
        fontSize: { xs: '0.9rem', sm: '1rem' },
        animation: 'fadeInUp 0.8s ease-out'
      }}
    >
      {language === 'ar' ? 'شكرا لك على الشراء' : 'Thank you for your purchase'}
    </Typography>
  </Box>
</Fade>

      <Grid container spacing={{ xs: 2, sm: 3 }} style={{ marginRight: -16 }}>
        {/* Order Summary Card */}
        <Grid item xs={12} md={8}>
          <Fade in timeout={600}>
            <Paper elevation={3} sx={{ 
              p: { xs: 2, sm: 3 }, 
              borderRadius: '20px',
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              transition: 'all 0.3s ease',
              '&:hover': {
                border: '1px solid #FFD700',
                boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)'
              }
            }}>
              {/* Order Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
                    color: '#FFD700',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {language === 'ar' ? 'ملخص طلبك' : 'Order Summary'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'طلب رقم #:' : '# Order ID:'} {order.id}
                  </Typography>
                  <Chip 
                    label={statusTxt[order.status]} 
                    sx={{ 
                      mt: 1,
                      backgroundColor: getStatusColor(order.status).bg,
                      color: getStatusColor(order.status).text,
                      fontWeight: 'bold',
                      border: 'none',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' }
                    }}
                  />
                </Box>
                <Typography variant="h5" sx={{ 
                  color: '#FFD700',
                  fontWeight: 800,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  {order.total_amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                </Typography>
              </Box>

              <Divider sx={{ my: 2, backgroundColor: '#2D2D2D' }} />

              {/* Savings Banner */}
              {totalSavings > 0 && (
                <Zoom in>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
                    p: { xs: 1.5, sm: 2.5 }, 
                    borderRadius: '16px', 
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}>
                    <Savings sx={{ fontSize: { xs: 32, sm: 48 }, color: '#000000' }} />
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: '#000000', 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        {language === 'ar' ? 'لقد وفرت ' : 'You save '}{totalSavings.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en} !
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#000000', opacity: 0.8 }}>
                        {language === 'ar' ? 'نسبة خصم ' : 'Discount value '} {((totalSavings / originalTotal) * 100).toFixed(1)}% {language === 'ar' ? 'من طلبك' : 'from your order'}
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              )}

              {/* Order Items */}
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: '#FFD700',
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                <ShoppingBag sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'منتجات الطلب' : 'Order items'}
              </Typography>
              
              <List sx={{ 
                backgroundColor: '#0D0D0D',
                borderRadius: '12px',
              }}>
                {order.items.map((item, index) => {
                  const originalPrice = item.original_price || item.price;
                  const isDiscounted = originalPrice > item.price;
                  const itemSavings = (originalPrice - item.price) * item.quantity;

                  return (
                    <Fade in timeout={300 + index * 100} key={item.id}>
                      <ListItem 
                        sx={{ 
                          py: 2,
                          borderBottom: '1px solid #2D2D2D',
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                          px: { xs: 0, sm: 1 }
                        }}
                      >
                        <Box sx={{ display: 'flex', width: '100%', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <Box sx={{ 
                            width: { xs: '100%', sm: 70 }, 
                            height: { xs: 120, sm: 70 }, 
                            backgroundColor: '#1A1A1A',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <img
                              src={item.product?.imageUrl || noPhoto}
                              alt={item.product?.name}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain',
                                borderRadius: '12px'
                              }}
                            />
                          </Box>
                          
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium" sx={{ color: '#FFFFFF', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                              {language === 'ar' ? item.product?.name : item.product?.name_en}
                            </Typography>
                            
                            {isDiscounted ? (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#FFD700', 
                                  fontWeight: 'bold',
                                  display: 'inline-block',
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}>
                                  {item.price} × {item.quantity} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    textDecoration: 'line-through', 
                                    color: '#6B6B6B',
                                    display: 'block'
                                  }}
                                >
                                  {originalPrice.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en} {language === 'ar' ? ' كل' : ' each'}
                                </Typography>
                                {item.discount_percent > 0 && (
                                  <Chip
                                    label={language === 'ar' ? `${item.discount_percent}% خصم` : `${item.discount_percent}% off`}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      backgroundColor: '#FFD700',
                                      color: '#000000',
                                      fontWeight: 'bold',
                                      height: 22,
                                      fontSize: '0.65rem'
                                    }}
                                  />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                {item.price} × {item.quantity} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                              </Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                              color: '#FFD700',
                              whiteSpace: 'nowrap',
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}>
                              {(item.price * item.quantity).toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                            </Typography>
                            {isDiscounted && (
                              <Typography variant="caption" sx={{ 
                                color: '#FFD700',
                                display: 'block',
                                fontWeight: 600
                              }}>
                                {language === 'ar' ? 'توفير ' : 'Discount '} {itemSavings.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    </Fade>
                  );
                })}
              </List>

              <Divider sx={{ my: 2, backgroundColor: '#2D2D2D' }} />

              {/* Order Totals */}
              <Box sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                backgroundColor: '#1A1A1A',
                border: '1px solid #2D2D2D',
                borderRadius: '16px'
              }}>
                {totalSavings > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                        {language === 'ar' ? 'القيمة الأساسية:' : 'Original value:'}
                      </Typography>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#6B6B6B' }}>
                        {originalTotal.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#FFD700' }}>
                        <Discount sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5, color: '#FFD700' }} />
                        {language === 'ar' ? 'إجمالي التوفير :' : 'Total discount :'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FFD700' }}>
                        -{totalSavings.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1, backgroundColor: '#2D2D2D' }} />
                  </>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{language === 'ar' ? 'رسوم التوصيل :' : 'Delivery fees :'}</Typography>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {order.delivery_fee} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{language === 'ar' ? 'رسوم الخدمة :' : 'Service charge :'}</Typography>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {order.service_charge} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#FFFFFF', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {language === 'ar' ? 'الإجمالي:' : 'Total:'}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#FFD700', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {order.total_amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {language === 'ar' ? 'إجمالي الطلب:' : 'Total order:'}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    sx={{ 
                      color: '#FFD700',
                      fontWeight: 900,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}
                  >
                    {order.total_amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Grid>

        {/* Order Details Sidebar */}
        <Grid item xs={12} md={4}>
          <Fade in timeout={700}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Shipping Information */}
              <Card elevation={3} sx={{ 
                borderRadius: '20px',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: '1px solid #FFD700',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(255, 215, 0, 0.15)'
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFD700',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    <LocalShipping sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'معلومات التوصيل' : 'Delivery Information'}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {language === 'ar' ? 'عنوان التوصيل:' : 'Delivery address:'}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      mt: 1, 
                      p: 2, 
                      backgroundColor: '#1A1A1A',
                      border: '1px solid #2D2D2D',
                      borderRadius: '12px',
                      color: '#FFFFFF',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      {order.shipping_address}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card elevation={3} sx={{ 
                borderRadius: '20px',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: '1px solid #FFD700',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(255, 215, 0, 0.15)'
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFD700',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    <Payment sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'معلومات الدفع' : 'Payment Information'}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {language === 'ar' ? 'طريقة الدفع:' : 'Payment method:'}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      mt: 0.5,
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      {order.payment_method.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {language === 'ar' ? 'تاريخ الطلب:' : 'Order date:'}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      mt: 0.5,
                      color: '#FFFFFF',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      {new Date(order.created_at).toLocaleDateString('ar-EG')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Action Buttons - Mobile Sticky */}
              <Card elevation={3} sx={{ 
                borderRadius: '20px',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                transition: 'all 0.3s ease',
                display: { xs: 'block', md: 'block' }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFD700',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    <Store sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'الإجراءات' : 'Actions'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={() => navigate('/')}
                      fullWidth
                      sx={{
                        color: '#FFFFFF',
                        borderColor: '#2D2D2D',
                        py: { xs: 1, sm: 1.5 },
                        borderRadius: '12px',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        '&:hover': {
                          borderColor: '#FFD700',
                          backgroundColor: 'rgba(255, 215, 0, 0.08)',
                          color: '#FFD700',
                        }
                      }}
                    >
                      {language === 'ar' ? 'إستكمال التسوق' : 'Continue Shopping'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Games />}
                      onClick={() => setGameOpen(true)}
                      sx={{ 
                        borderColor: '#FFD700', 
                        color: '#FFD700',
                        py: { xs: 1, sm: 1.5 },
                        borderRadius: '12px',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {language === 'ar' ? 'العب مع صديق' : 'Play with Friend'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Grid>
      </Grid>
{/* Sticky Mobile Buttons - Clean Version */}
<Zoom in={isMobile && isSticky} timeout={300}>
  <Paper
    elevation={6}
    sx={{
      position: 'fixed',
      bottom: 60,
      left: 0,
      right: 0,
      zIndex: 1100,
      backgroundColor: '#0D0D0D',
      borderTop: '2px solid #FFD700',
      borderRadius: 0,
      p: 1.5,
      display: { xs: 'flex', md: 'none' },
      gap: 1,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
    }}
  >
    <Button
      variant="outlined"
      fullWidth
      startIcon={<ArrowBack />}
      onClick={() => navigate('/')}
      sx={{
        color: '#FFFFFF',
        borderColor: '#2D2D2D',
        py: 1,
        borderRadius: '12px',
        fontSize: '0.75rem',
        '&:hover': {
          borderColor: '#FFD700',
          backgroundColor: 'rgba(255, 215, 0, 0.08)',
          color: '#FFD700',
        }
      }}
    >
      {language === 'ar' ? 'إستكمال التسوق' : 'Continue'}
    </Button>
    
    <Button
      variant="contained"
      fullWidth
      startIcon={<Games />}
      onClick={() => setGameOpen(true)}
      sx={{
        background: 'linear-gradient(135deg, #FFD700 0%, #F5C800 100%)',
        color: '#000000',
        py: 1,
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 800,
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(255,215,0,0.4)',
        }
      }}
    >
      {language === 'ar' ? 'العب XO' : 'Play Game'}
      
    </Button>
  </Paper>
</Zoom>

<style>
  {`
    @keyframes pulse {
      0% { transform: scale(0.9); opacity: 0.7; }
      70% { transform: scale(1.1); opacity: 1; }
      100% { transform: scale(0.9); opacity: 0.7; }
    }
  `}
</style>
    

      <MultiplayerTicTacToe
        open={gameOpen}
        onClose={() => setGameOpen(false)}
        language={language}
        currentTableId={order?.table_number}
        currentTableName={order?.table_number ? `Table ${order?.table_number}` : 'Guest'}
        user={user}
      />
    </Container>
  );
};

export default OrderConfirmation;